import { Button } from '@mui/material';
import { useQuery } from '@redwoodjs/web';
import dayjs from 'dayjs';
import { useField } from 'formik';
import { useEffect, useMemo } from 'react';
import { ConfirmationModal } from 'src/components/Modals';
import { showErrorLong } from 'src/components/Toasts/toast';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { GET_PAYMENT_METHODS } from 'src/graphql/queries/getPaymentMethods.query';
import { onError } from 'src/utils/utils';
import AllocatePaymentModalContent from './AllocatePaymentModalContent';

type AllocatePaymentCallToActionProps = {
    readonly totalOutstanding: number;
    readonly allocating: boolean;
    readonly setShowPaymentAllocationModel: (value: boolean) => void;
    readonly showPaymentAllocationModal: boolean;
    readonly onAllocatePayment: () => void;
};

export default function SalesInvoiceAllocatePaymentCallToAction({
    allocating,
    setShowPaymentAllocationModel,
    showPaymentAllocationModal,
    totalOutstanding,
    onAllocatePayment,
}: AllocatePaymentCallToActionProps) {
    const today = dayjs();
    const { usersCurrency } = useAuthContext();
    const [{ value: status }] = useField('status');
    const [{ value: date }, { error: dateError }, { setValue: setDateValue }] = useField('date');

    const { data, error } = useQuery(GET_PAYMENT_METHODS, {
        onError,
        fetchPolicy: 'cache-first',
    });

    useEffect(() => {
        if (error?.message) {
            showErrorLong(error.message);
        }
    }, [error]);

    const paymentMethods = useMemo(() => {
        const methods = data?.getPaymentMethods?.map((method) => {
            return {
                label: method.displayed_as,
                value: method.id,
            };
        }, []);
        const filteredPaymentMethods = methods?.filter((method) => {
            return method.value === 'CREDIT_DEBIT';
        });
        filteredPaymentMethods?.push(
            { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
            { label: 'Direct Debit', value: 'DIRECT_DEBIT' }
        );
        return filteredPaymentMethods;
    }, [data]);

    const onCancel = () => {
        setShowPaymentAllocationModel(false);
    };

    const onConfirm = () => {
        onAllocatePayment();
    };

    return (
        <>
            <Button
                variant="npi-primary"
                onClick={() => {
                    return setShowPaymentAllocationModel(true);
                }}
            >
                Allocate Payment
            </Button>
            <ConfirmationModal
                isOpen={showPaymentAllocationModal}
                title="Allocate Payment"
                modalWidth="30%"
                confirmText={allocating ? 'Allocating...' : 'Confirm'}
                message={
                    <AllocatePaymentModalContent
                        usersCurrency={usersCurrency}
                        totalOutstanding={totalOutstanding}
                        allocating={allocating}
                        status={status}
                        paymentMethods={paymentMethods}
                        maxDate={today}
                        dateValue={!date ? today : date}
                        dateError={dateError}
                        updateDate={setDateValue}
                    />
                }
                onCancel={onCancel}
                onConfirm={onConfirm}
            />
        </>
    );
}
